var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { CustomElement, PacemEventTarget, Watch, P, PropertyConverters, Utils } from './_default-imports';
import { CustomTypedEvent } from '../../events';
//namespace Pacem {
export const RouterNavigatingEventName = 'navigating';
export const RouterNavigateEventName = 'navigate';
export class RouterNavigateEvent extends CustomTypedEvent {
    constructor(path) {
        super(RouterNavigateEventName, typeof path === 'string' ? { path } : path, { cancelable: false, bubbles: false });
    }
}
export class RouterNavigatingEvent extends CustomTypedEvent {
    constructor(path) {
        super(RouterNavigatingEventName, path, { cancelable: true, bubbles: false });
    }
}
//}
//namespace Pacem.Components {
const CHECK_PATTERN = /^([\w\.]:)?\/\/[^\/]+/;
let PacemRouterElement = class PacemRouterElement extends PacemEventTarget {
    constructor() {
        super(...arguments);
        this._onPopState = (evt) => {
            if (!Utils.isNull(evt.state) && !this.#cancelingNavigation) {
                this.path = evt.state.$path;
            }
        };
    }
    #cancelingNavigation;
    #template;
    parseState(state = {}) {
        const path = this._stringifyMergedState(state);
        const segments = this._segmentateUrl(path);
        return this._parseState(segments.path + segments.query + segments.hash, segments.path, segments.query, segments.hash);
    }
    navigate(path, title) {
        if (this.disabled || Utils.isNullOrEmpty(path)) {
            return;
        }
        if (typeof path === 'object') {
            path = this._stringifyMergedState(path);
        }
        else {
            // ease state reutilization
            path = Utils.URIs.format(path, this.state);
        }
        if (path != this.path) {
            this.path = path;
            return;
        }
        const l = document.location;
        if (CHECK_PATTERN.test(path)) {
            if (!path.startsWith(l.protocol + '//' + l.host)) {
                throw `Only same-origin navigation is currently supported. "${path}" is not a valid path.`;
            }
        }
        if (!this.#cancelingNavigation) {
            // coming from path...
            const from = this.state && this.state.$path || void 0;
            // processing new segments and state
            const segments = this._segmentateUrl(path), state = this._parseState(segments.path + segments.query + segments.hash, segments.path, segments.query, segments.hash);
            // popping state? aka history.back()/.forward()/.go(...)?
            // means that the URL is already in the target fashion
            const poppingState = (l.pathname + l.search + l.hash) === segments.path + segments.query + segments.hash;
            const evt = new RouterNavigatingEvent(path);
            window.dispatchEvent(evt);
            if (this.#cancelingNavigation = evt.defaultPrevented) {
                // this will cut the history stack, no better option tho...
                if (poppingState) {
                    // re-push current state
                    window.history.pushState(this.state, title, from);
                }
                this.path = from;
                // exiting
                return;
            }
            // this one actually does trigger the navigation in the outer world
            this.state = state;
            if (poppingState) {
                window.history.scrollRestoration = 'manual';
                window.history.replaceState(state, title);
            }
            else {
                window.history.pushState(state, title, path);
            }
            if (!Utils.isNullOrEmpty(title)) {
                document.title = title;
            }
            window.dispatchEvent(new RouterNavigateEvent({ path, referrer: from, title }));
        }
        this.#cancelingNavigation = false;
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        if (!first) {
            switch (name) {
                case 'template':
                    this.#template = null;
                    break;
                case 'path':
                    this.navigate(val);
                    break;
            }
        }
    }
    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('popstate', this._onPopState, false);
    }
    viewActivatedCallback() {
        super.viewActivatedCallback();
        const p = this.path;
        if (!Utils.isNullOrEmpty(p)) {
            this.navigate(p);
        }
    }
    disconnectedCallback() {
        window.removeEventListener('popstate', this._onPopState, false);
        super.disconnectedCallback();
    }
    /**
     * Merged to provided state into the current one and returns the resulting path (including current querystring and hash).
     * @param state Piece of new state to be merged
     */
    _stringifyMergedState(state) {
        const stateCopy = Utils.extend({}, state);
        const newState = Utils.extend({}, this.state, stateCopy);
        const tmpl = this.template;
        let retval = '' + tmpl;
        for (let item of this._parseTemplate(tmpl)) {
            const prop = item.name;
            const pattern = new RegExp("\/\{" + prop + "\\??\}");
            const value = newState[prop];
            delete stateCopy[prop];
            retval = retval.replace(pattern, Utils.isNullOrEmpty(value) ? '' : ('/' + value));
        }
        // leftovers appended as query parameters
        const q = Utils.extend({}, this.state.$query);
        for (let prop in stateCopy) {
            q[prop] = stateCopy[prop];
        }
        const { hash } = this._segmentateUrl(this.path);
        return Utils.URIs.appendQuery(retval, q, true) + hash;
    }
    _parseTemplate(tmpl = this.template) {
        if (!Utils.isNullOrEmpty(this.#template)) {
            return this.#template;
        }
        return this.#template = Utils.URIs.parseRoute(tmpl);
    }
    _segmentateUrl(url) {
        const retval = Utils.URIs.split(url);
        if (Utils.isNullOrEmpty(retval.path)) {
            return null;
        }
        return retval;
    }
    _parseState(fullPath, path, query, hash) {
        const tmpl = this._parseTemplate();
        return Utils.URIs.parseState(tmpl, fullPath, path, query, hash);
    }
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemRouterElement.prototype, "template", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Eval })
], PacemRouterElement.prototype, "state", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemRouterElement.prototype, "path", void 0);
PacemRouterElement = __decorate([
    CustomElement({ tagName: P + '-router' })
], PacemRouterElement);
export { PacemRouterElement };
//}
